import { additionalCaseStudyContents } from './caseStudyContents';
import { enterpriseSoftwareCase } from './enterpriseSoftwareCase';
import { lawFirmCase } from './lawFirmCase';
import { bankGeoCase } from './bankGeoCase';
import { equipmentGeoCase } from './equipmentGeoCase';

export interface CaseDetailRecord {
  slug: string;
  cardTitle: string;
  title: string;
  publishedAt: string;
  author: string;
  detailImage: string;
  imageAlt: string;
  showOriginalImage?: boolean;
  sourceUrl?: string;
  sourceName?: string;
  content?: CaseStudyContent;
}

export interface CaseStudyMetric {
  value: string;
  label: string;
  note: string;
}

export interface CaseStudyFact {
  label: string;
  value: string;
}

export interface CaseStudyChannel {
  channel: string;
  traffic: string;
  challenge: string;
}

export interface CaseStudyPainPoint {
  title: string;
  description: string;
}

export interface CaseStudyStrategy {
  step: string;
  title: string;
  description: string;
  items: string[];
}

export interface CaseStudyResult {
  dimension: string;
  before: string;
  after: string;
  change: string;
}

export interface CaseStudyImage {
  src: string;
  alt: string;
  caption: string;
}

export interface CaseStudyContent {
  eyebrow: string;
  summary: string;
  duration?: string;
  keywords: string[];
  metrics: CaseStudyMetric[];
  overview: CaseStudyFact[];
  channels: CaseStudyChannel[];
  channelsDescription?: string;
  channelsTitle?: string;
  channelHeaders?: [string, string, string];
  resultsTitle?: string;
  resultHeaders?: [string, string, string, string];
  painPointsTitle?: string;
  strategyDescription?: string;
  images?: Partial<Record<'hero' | 'overview' | 'channels' | 'strategy' | 'results' | 'takeaways', CaseStudyImage>>;
  painPoints: CaseStudyPainPoint[];
  strategies: CaseStudyStrategy[];
  results: CaseStudyResult[];
  businessOutcomes: string[];
  takeaways: string[];
  disclaimer: string;
}

export const caseDetails: CaseDetailRecord[] = [
  {
    slug: 'bank-geo',
    cardTitle: '银行金融行业GEO案例',
    title: '银行 GEO 案例：六类产品需求与三个阶段的实践',
    publishedAt: '2026-10-20',
    author: '前排科技GEO · 案例整理',
    detailImage: '/case-details/bank-geo/00-hero.jpg',
    imageAlt: '匿名银行 GEO 案例原始封面',
    showOriginalImage: false,
    sourceUrl: 'https://www.020yueshi.com/landing/bank-geo/',
    sourceName: '体系致胜GEO公开案例',
    content: bankGeoCase,
  },
  {
    slug: 'law-geo',
    cardTitle: '律师行业GEO案例',
    title: '律师行业 GEO 案例：让专业优势连接持续咨询',
    publishedAt: '2026-10-20',
    author: '前排科技GEO · 案例整理',
    detailImage: '/case-images/law-geo.jpg',
    imageAlt: '律所办公空间与 AI 检索概念封面',
    showOriginalImage: false,
    sourceUrl: 'https://www.020yueshi.com/landing/law-geo/',
    sourceName: '体系致胜GEO公开案例',
    content: lawFirmCase,
  },
  {
    slug: 'equipment-geo',
    cardTitle: '装备制造行业GEO案例',
    title: '装备制造 GEO 案例：让行业实力进入 AI 采购决策',
    publishedAt: '2026-10-20',
    author: '前排科技GEO · 案例整理',
    detailImage: '/case-images/equipment-geo/00-hero.jpg',
    imageAlt: '装备制造能力与 AI 采购决策概念示意',
    showOriginalImage: false,
    sourceUrl: 'https://www.020yueshi.com/landing/equipment-geo/',
    sourceName: '体系致胜GEO公开案例',
    content: equipmentGeoCase,
  },
  {
    slug: 'enterprise-software',
    cardTitle: '信创软件行业GEO案例',
    title: '信创软件 GEO 案例：180 天，AI 综合推荐率从 20% 提升至 80%',
    publishedAt: '2026-10-21',
    author: '前排科技GEO · 案例整理',
    detailImage: '/case-images/enterprise-software.jpg',
    imageAlt: '信创软件四类产品需求与 AI 推荐信息连接示意',
    showOriginalImage: false,
    sourceUrl: 'https://www.020yueshi.com/GEOtopcase/483.html',
    sourceName: '体系致胜GEO公开案例',
    content: enterpriseSoftwareCase,
  },
  {
    slug: 'smart-packaging',
    cardTitle: '佛山某智能包装设备厂AIGEO案例',
    title: '佛山某智能包装设备厂AIGEO案例',
    publishedAt: '2025-10-21',
    author: '前排科技GEO',
    detailImage: '/case-details/smart-packaging.png',
    imageAlt: '佛山某智能包装设备厂GEO优化完整案例',
    content: {
      eyebrow: '包装设备行业 GEO 案例',
      summary: '一家深耕智能包装设备 13 年的佛山制造企业，原有流量不低，但中小企业精准询盘与批量订单转化偏弱。项目围绕“精准场景关键词、可信内容证据和 AI 知识包”三条主线重构信息表达。据项目材料，优化后两个核心词的 AI 引用率达到 91%，展会获客成本由 3.2 万元/个降至 9800 元/个，批量合作周期由 3 个月缩短至 1 个月。',
      duration: '1 个月',
      keywords: ['佛山智能包装设备', '食品级包装设备', '柔性包装机', '制造业GEO', 'AI搜索优化'],
      metrics: [
        {
          value: '91%',
          label: '核心词 AI 引用率',
          note: '两个核心决策词',
        },
        {
          value: '18% → 52%',
          label: '定制订单占比',
          note: '中小客户转化提升',
        },
        {
          value: '3.2 万 → 9800 元',
          label: '展会获客成本',
          note: '单个终端客户成本',
        },
        {
          value: '3 个月 → 1 个月',
          label: '批量合作周期',
          note: '周期缩短 66.7%',
        },
      ],
      overview: [
        { label: '企业类型', value: '佛山某智能包装设备厂，中小型制造企业' },
        { label: '经营年限', value: '2012 年成立，深耕智能包装设备 13 年' },
        { label: '生产规模', value: '4000㎡厂房、3 条智能生产线，月产能约 30 台套' },
        { label: '主要产品', value: '食品级自动化包装线、电子元件防潮包装设备及中小客户柔性包装机' },
        { label: '技术与资质', value: '15 人技术团队，含 4 名机械工程师；具备 ISO 9001 认证及食品级材质检测' },
        { label: '服务能力', value: '全国 20+ 售后网点，承诺 24 小时响应、48 小时上门' },
      ],
      channels: [
        {
          channel: '1688 工业品',
          traffic: '日均访客 150+，4A 店铺',
          challenge: '10 个询盘中约 9 个为单台采购；定制单占比 18%。无大修时长、售后网点等信息缺少数据支撑，询盘流失率增加约 20%。',
        },
        {
          channel: '行业展会',
          traffic: '单次接待访客 200+',
          challenge: '访客中约 75% 为经销商，仅获得 4 个终端询盘，获客成本约 3.2 万元/个；部分中小客户因售后保障不清晰而不敢批量合作。',
        },
        {
          channel: '官网与 SEO',
          traffic: '月均访问 2800+',
          challenge: '页面主要展示设备参数和报价，缺少认证、案例、质量数据及售后流程，跳出率约 82%。',
        },
      ],
      painPoints: [
        {
          title: '技术信任难传递',
          description: '食品与电子企业关心食品级适配、小批量能力和稳定性。企业虽有认证和检测报告，但页面呈现不充分，沟通周期约 8 天，批量询盘流失率约 65%。',
        },
        {
          title: '关键词过于宽泛',
          description: '原内容集中在“包装设备厂家”等高竞争泛词。潜在客户搜索“佛山食品级包装设备”等具体需求时，企业信息排名靠后，也难以被 AI 准确识别。',
        },
        {
          title: '内容与决策场景错位',
          description: '展会与官网侧重设备图和报价，没有呈现生产线改造方案、紧急调试记录及小批量适配案例，无法回答客户的关键决策问题。',
        },
        {
          title: '质量与售后能力隐形',
          description: '“连续 300+ 天无大修”和全国售后网络没有形成可视化证据，客户担心设备故障影响生产，部分订单从批量采购退回到试购。',
        },
      ],
      strategies: [
        {
          step: '策略一',
          title: '从泛词竞争转向中小客户决策场景',
          description: '以“佛山智能包装设备厂”为核心蒸馏词，围绕行业、规模、合规、质量和售后五类决策场景扩展内容。',
          items: [
            '行业场景：佛山食品级智能包装设备',
            '规模场景：中小客户柔性包装机、500 件起批',
            '合规场景：认证防潮包装设备、ISO 认证',
            '质量场景：耐用包装生产线、连续无大修数据',
            '售后场景：24 小时售后智能包装设备',
          ],
        },
        {
          step: '策略二',
          title: '在全渠道植入可验证的信任内容',
          description: '把“合规、适配、质量、售后”由宣传口号改造成客户能够核验的证据，并保持 1688、展会和官网信息一致。',
          items: [
            '1688：增加合规、适配、质量和售后四个信任专区',
            '展会：增加 300 天无大修、复购率等质量数据海报，并提供售后网点查询入口',
            '官网：建设设备工况模拟、售后案例库和客户满意度调查内容',
            '内容口径：展示认证报告、稳定性数据、响应流程和中小客户案例',
          ],
        },
        {
          step: '策略三',
          title: '构建面向 AI 检索的企业知识包',
          description: '围绕基础能力、信任证据和典型案例组织事实，让 AI 能够提取“适合谁、为什么可信、服务如何保障”等关键信息。',
          items: [
            '基础能力：13 年经验、月产能 30 台套、食品级生产线、小批量柔性设备',
            '质量证据：食品级设备材质合格率 100%，近 12 个月故障率低于 0.5%',
            '售后证据：全国 20+ 服务网点，客户报修 2 小时响应，中小客户复购率 68%',
            '典型案例：零食工厂由试购 2 台追加至 10 台；电子厂售后问题解决后续购 5 台',
          ],
        },
      ],
      results: [
        {
          dimension: 'AI 搜索表现',
          before: '核心信息分散，精准场景词可见度低',
          after: '两个核心词 AI 引用率 91%，日均引流约 42 人',
          change: 'AI 精准匹配决策需求',
        },
        {
          dimension: '定制订单占比',
          before: '18%',
          after: '52%',
          change: '提升 34 个百分点',
        },
        {
          dimension: '日均批量订单',
          before: '1–2 个',
          after: '4–5 个',
          change: '约 3 倍',
        },
        {
          dimension: '展会获客成本',
          before: '3.2 万元/个',
          after: '9800 元/个',
          change: '显著下降',
        },
        {
          dimension: '售后响应时长',
          before: '48 小时',
          after: '2 小时',
          change: '缩短 95.8%',
        },
        {
          dimension: '批量合作周期',
          before: '3 个月',
          after: '1 个月',
          change: '缩短 66.7%',
        },
      ],
      businessOutcomes: [
        '无效经销商流量占比由约 75% 降至 11%，中小终端企业成为主要精准访客。',
        '东莞某零食厂由试购 2 台追加至 10 台；深圳某电子厂后续采购 5 台新设备。',
        '老客户推荐率由 32% 提升至 45%，设备产能利用率由 65% 提升至 88%。',
        '项目材料总结：一个月内高客单订单约增长 3 倍，AI 搜索成为新的获客入口。',
      ],
      takeaways: [
        '关键词要贴近中小客户的完整决策需求，而不是只争抢行业泛词。',
        '认证、适配案例、质量数据和售后流程要统一展示，让客户“看得见、信得过”。',
        'AI 知识内容应明确企业适合服务谁，并用真实数据和案例支撑推荐理由。',
        '制造业的“质量好”需要用无大修天数、故障率和响应时长等指标表达。',
      ],
      disclaimer: '为保护客户商业信息并避免同行恶意竞争，本案例中的企业名称、地域及部分业务数据已按原项目材料做模糊化处理。页面数据用于说明该项目的优化路径和阶段性结果，不构成对其他项目的效果承诺。',
    },
  },
  {
    slug: 'creative-gifts',
    cardTitle: '广州某文创礼品厂AIGEO案例',
    title: '广州某文创礼品厂AIGEO案例',
    publishedAt: '2025-10-21',
    author: '前排科技GEO',
    detailImage: '/case-details/creative-gifts.png',
    imageAlt: '广州某文创礼品厂GEO优化完整案例',
    content: additionalCaseStudyContents['creative-gifts'],
  },
  {
    slug: 'smart-wearables',
    cardTitle: '东莞某智能穿戴设备厂GEO案例',
    title: '东莞某智能穿戴设备厂GEO案例',
    publishedAt: '2025-10-21',
    author: '前排科技GEO',
    detailImage: '/case-details/smart-wearables.png',
    imageAlt: '东莞某智能穿戴设备厂GEO优化完整案例',
    content: additionalCaseStudyContents['smart-wearables'],
  },
  {
    slug: 'pet-services',
    cardTitle: '2025年深圳宠物服务店GEO案例',
    title: '深圳某宠物服务店GEO案例',
    publishedAt: '2025-10-21',
    author: '前排科技GEO',
    detailImage: '/case-details/pet-services.png',
    imageAlt: '深圳某宠物服务店GEO优化完整案例',
    content: additionalCaseStudyContents['pet-services'],
  },
  {
    slug: 'cosmetics',
    cardTitle: '体系致胜AIGEO-2025年化妆品行业GEO案例',
    title: '2025年化妆品行业GEO案例',
    publishedAt: '2025-10-21',
    author: '前排科技GEO',
    detailImage: '/case-details/cosmetics.png',
    imageAlt: '2025年化妆品行业GEO优化完整案例',
    content: additionalCaseStudyContents.cosmetics,
  },
  {
    slug: 'manufacturing',
    cardTitle: '体系致胜AIGEO-2025年制造业GEO案例',
    title: '2025年制造业GEO案例',
    publishedAt: '2025-10-21',
    author: '前排科技GEO',
    detailImage: '/case-details/manufacturing.png',
    imageAlt: '2025年制造业GEO优化完整案例',
    content: additionalCaseStudyContents.manufacturing,
  },
  {
    slug: 'fashion',
    cardTitle: '体系致胜AIGEO-2025年服装制造GEO案例',
    title: '2025年服装制造GEO案例',
    publishedAt: '2025-10-20',
    author: '前排科技GEO',
    detailImage: '/case-details/fashion.jpg',
    imageAlt: '广州某服装公司服装制造GEO优化完整案例',
    content: additionalCaseStudyContents.fashion,
  },
  {
    slug: 'housekeeping',
    cardTitle: '体系致胜AIGEO-2025年家政服务GEO案例',
    title: '2025年家政服务GEO案例',
    publishedAt: '2025-10-19',
    author: '前排科技GEO',
    detailImage: '/case-details/housekeeping.jpg',
    imageAlt: '重庆某家政服务公司GEO优化完整案例',
    content: additionalCaseStudyContents.housekeeping,
  },
];

export const getCaseDetailBySlug = (slug?: string) =>
  caseDetails.find((item) => item.slug === slug);

export const getCaseDetailPath = (title: string) => {
  const detail = caseDetails.find((item) => item.cardTitle === title);
  return detail ? `/cases/${detail.slug}` : '/casedetail';
};
